/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React from 'react';

import { Flex, Box } from 'components/styled';
import Text from 'components/styled/Text';
import RTText from 'components/form/richtext/RTText';
import Paper from 'components/styled/Paper';

import { getFile } from 'api/fileApi';

import { DescriptionPage as DescriptionPageT } from 'typing/endpoints';
type ViewDescriptionPage = {
	variant: 'student';
	titleNode?: never;
	input?: never;
} & Partial<Pick<DescriptionPageT, 'content' | 'alignment' | 'photo'>>;

type EditableDescriptionPage = {
	variant: 'editable';
	input: React.ReactNode;
	required?: boolean;
	titleNode?: React.ReactNode;
};

type Props = Partial<Pick<DescriptionPageT, 'title'>> &
	(ViewDescriptionPage | EditableDescriptionPage);

const DescriptionPage = ({
	title,
	...props
}: Props & React.ComponentProps<typeof Paper>) =>
	props.variant === 'student' ? (
		<Flex flexDirection="column" {...props}>
			<Text
				mt={0}
				mb={4}
				fontSize="xl"
				textAlign="center"
				color="primary"
				fontWeight="bold"
			>
				{title}
			</Text>
			<Flex
				alignItems={props.alignment === 'BOTTOM' ? 'center' : 'flex-start'}
				flexDirection={[
					'column',
					// eslint-disable-next-line no-nested-ternary
					props.alignment === 'BOTTOM'
						? 'column'
						: props.alignment === 'LEFT'
						? 'row'
						: 'row-reverse',
				]}
			>
				<Box flexGrow={1}>
					<RTText value={props.content} />
				</Box>
				{props.photo && (
					<Box
						mt={props.alignment === 'BOTTOM' ? 3 : 0}
						ml={[0, props.alignment === 'LEFT' ? 3 : 0]}
						mr={[0, props.alignment === 'RIGHT' || !props.alignment ? 3 : 0]}
						width={[1, 0.5]}
						flexShrink={0}
					>
						<img
							src={getFile(props.photo)}
							alt=""
							css={css`
								width: 100%;
								position: static;
								height: 100%;
								object-fit: cover;
							`}
						/>
					</Box>
				)}
			</Flex>
		</Flex>
	) : (
		<Paper flexDirection="row" alignItems="stretch" flexWrap="wrap" {...props}>
			<Flex width={['100%', '100%', '25%']} pr={3} flexDirection="column">
				{props.titleNode ?? (
					<Text fontSize="xl" color="primary" fontWeight="bold">
						{title}
						{props.required && (
							<Text as="span" color="error">
								*
							</Text>
						)}
					</Text>
				)}
			</Flex>
			<Flex
				width={['100%', '100%', '75%']}
				mt={[3, 3, 0]}
				flexDirection="column"
				backgroundColor="white"
			>
				{props.input}
			</Flex>
		</Paper>
	);

export default DescriptionPage;
